/**
 * utils/statementLedger.ts
 * Customer statements: one ledger per CONTACT ID, with a running balance.
 *
 * Grouped by id, never by name — two customers called "Ravi" are two ledgers.
 * `dots` carries the row colours so this stays a pure function.
 */

export interface StatementRow { date: string; ref: string; kind: string; debit: number; credit: number; run: number; dot: string; }
export interface StatementContact { id: string; name: string; meta: string; invoiced: number; received: number; credited: number; sub: string; rows: StatementRow[]; }
export interface StatementDots { info: string; danger: string; accent: string; success: string; }

const money = (v: unknown): number => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const ts = (d: string): number => {
  const t = new Date(d).getTime();
  return isNaN(t) ? 0 : t;
};

type Contact = StatementContact;

export function buildStatements(docs: any[], credits: any[], payments: any[], dots: StatementDots): StatementContact[] {
  // Invoices & credit notes carry contact_id AND contact_name; payments carry
  // only contact_id. Build an id→name map so a payment groups under the same
  // named contact as its invoices instead of a stray "ctc_…" row.
  const idToName = new Map<string, string>();
  [...docs, ...credits].forEach((d) => { if (d.contact_id && d.contact_name) idToName.set(d.contact_id, d.contact_name); });
  // Grouped by contact ID, never by name: two customers called "Ravi" are
  // two ledgers. The name is only the label.
  const map = new Map<string, Contact>();
  const get = (x: any): Contact => {
    const id: string = x.contact_id || `name:${x.contact_name || 'Unknown contact'}`;
    let c = map.get(id);
    if (!c) {
      const name = x.contact_name || idToName.get(x.contact_id) || x.contact_id || 'Unknown contact';
      c = { id, name, meta: '', invoiced: 0, received: 0, credited: 0, sub: '', rows: [] };
      map.set(id, c);
    }
    return c;
  };
  docs.forEach((d) => {
    if (['VOID', 'DRAFT'].includes(d.status)) return;
    const c = get(d);
    const amt = money(d.grand_total);
    c.invoiced += amt;
    const overdue = d.status === 'OVERDUE';
    c.rows.push({ date: d.doc_date || '', ref: d.doc_number || 'Draft', kind: overdue ? 'Invoice · overdue' : 'Invoice', debit: amt, credit: 0, run: 0, dot: overdue ? dots.danger : dots.info });
  });
  credits.forEach((cn) => {
    if (['VOID', 'DRAFT'].includes(cn.status)) return;
    const c = get(cn);
    const amt = money(cn.grand_total);
    c.credited += amt;
    c.rows.push({ date: cn.doc_date || '', ref: cn.doc_number || 'Draft', kind: 'Credit note', debit: 0, credit: amt, run: 0, dot: dots.accent });
  });
  // The API lists newest first; walk oldest first so same-day payments run in order.
  [...payments].reverse().forEach((p) => {
    const c = get(p);
    const amt = money(p.amount);
    c.received += amt;
    c.rows.push({ date: p.payment_date || '', ref: (p.id || '').slice(0, 8) || 'PMT', kind: 'Payment', debit: 0, credit: amt, run: 0, dot: dots.success });
  });
  const list = Array.from(map.values());
  list.forEach((c) => {
    c.rows.sort((a, b) => ts(a.date) - ts(b.date));
    let run = 0;
    c.rows.forEach((r) => {
      run += r.debit - r.credit;
      r.run = run;
    });
    const bal = c.invoiced - c.received - c.credited;
    c.sub = bal <= 0 ? 'settled' : c.rows.some((r) => r.dot === dots.danger) ? 'overdue' : `${c.rows.filter((r) => r.debit > 0).length} open`;
  });
  return list.sort((a, b) => b.invoiced - a.invoiced);
}

/**
 * How a statement names its bottom line. A customer who has paid or been
 * credited more than invoiced is IN CREDIT — "Balance due −₹14,160" read as if
 * they still owed money.
 */
export function balanceLabel(balance: number): { label: 'Balance due' | 'In credit'; amount: number } {
  // Sub-paisa float dust (e.g. -0.0000001) is a settled zero, not a credit.
  if (balance < -0.005) return { label: 'In credit', amount: -balance };
  return { label: 'Balance due', amount: Math.max(balance, 0) };
}
